<template>
  <div class="card-body ui-schedule">
    <div class="schedule-row">
      <div class="schedule-left" @click="handlePublishToggle">
        <Switch v-if="showPublishSwitch" :checked="publishEnabled" />
        <div class="schedule-label">Start date</div>
      </div>
      <div class="schedule-right" :class="{'has-datepicker': publishEnabled}">
        <DateTimePicker 
          v-if="publishEnabled" 
          :key="publishDateKey"
          :date="publishDate?.date || ''" 
          :time="publishDate?.time || ''"
          :datetime="publishDate?.val || ''" 
          :format="'MM/DD/YYYY'" 
          :spec_val="'publish'"
          @update-date="handlePublishDateUpdate"
          @update-time="handlePublishTimeUpdate" 
          :spec_date="true"
          :min_date="computedMinPublishDate"
          :max_date="computedMaxPublishDate"
        />
        <p class="schedule-utc-text" v-if="publishEnabled">This curation will automatically publish at the upper UTC time</p>
        <p class="schedule-status" v-else-if="publishedAtText">{{ publishedAtText }}</p>
        <p v-else class="schedule-status">Now</p>
      </div>
    </div>

    <div class="schedule-row">
      <div class="schedule-left" @click="handleDisableToggle">
        <Switch v-if="showDisableSwitch" :checked="disableEnabled" />
        <div class="schedule-label">End date</div>
      </div>
      <div class="schedule-right" :class="{'has-datepicker': disableEnabled}">
        <DateTimePicker 
          v-if="disableEnabled" 
          :key="disableDateKey"
          :date="disableDate?.date || ''" 
          :time="disableDate?.time || ''"
          :datetime="disableDate?.val || ''" 
          :format="'MM/DD/YYYY'" 
          :spec_val="'disable'"
          @update-date="handleDisableDateUpdate"
          @update-time="handleDisableTimeUpdate"
          :min_date="computedMinDisableDate"
        />
        <p class="schedule-utc-text" v-if="disableEnabled">This curation will automatically disable at the upper UTC time</p>
        <p class="schedule-status" v-else-if="!disabledAtText && !disableEnabled">Not set</p>
        <p class="schedule-status" v-else-if="disabledAtText">{{ disabledAtText }}</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import Switch from '@/components/ui/Switch.vue';
import DateTimePicker from '@/components/ui/DateTimePicker.vue';
import moment from 'moment';

const props = defineProps({
  // Publish (Start date) props
  publishEnabled: {
    type: Boolean,
    default: false
  },
  publishDate: {
    type: Object,
    default: () => ({ date: '', time: '', val: '' })
  },
  showPublishSwitch: {
    type: Boolean,
    default: true
  },
  minPublishDate: {
    type: Date,
    default: () => new Date()
  },
  publishedAtText: {
    type: String,
    default: ''
  },
  
  // Disable (End date) props
  disableEnabled: {
    type: Boolean,
    default: false
  },
  disableDate: {
    type: Object,
    default: () => ({ date: '', time: '', val: '' })
  },
  showDisableSwitch: {
    type: Boolean,
    default: true
  },
  minDisableDate: {
    type: Date,
    default: () => new Date()
  },
  disabledAtText: {
    type: String,
    default: ''
  }
});

const emit = defineEmits([
  'toggle:publish',
  'toggle:disable',
  'update:publishDate',
  'update:publishTime',
  'update:disableDate',
  'update:disableTime'
]);

// Get current UTC date and time (down to minutes)
// Create a local Date object with UTC time values
const getCurrentUTCDate = () => {
  const utcMoment = moment.utc();
  return new Date(
    utcMoment.year(),
    utcMoment.month(),
    utcMoment.date(),
    utcMoment.hours(),
    utcMoment.minutes()
  );
};

// Computed min and max dates
const computedMinPublishDate = computed(() => {
  return getCurrentUTCDate();
});

const computedMaxPublishDate = computed(() => {
  if (props.disableEnabled && props.disableDate?.date) {
    return new Date(props.disableDate.date);
  }
  return null;
});

const computedMinDisableDate = computed(() => {
  if (props.publishEnabled && props.publishDate?.date && props.publishDate?.time) {
    // Combine date and time from publishDate
    const dateStr = moment(props.publishDate.date).format('YYYY-MM-DD');
    const timeStr = props.publishDate.time;
    const publishDateTime = moment(`${dateStr} ${timeStr}`, 'YYYY-MM-DD h:mm A');
    return publishDateTime.toDate();
  }
  return getCurrentUTCDate();
});

// Computed keys to force re-render when date values change
const publishDateKey = computed(() => props.publishDate?.val || '0');
const disableDateKey = computed(() => props.disableDate?.val || '0');

// Helper function to validate and adjust disable date/time if needed
const validateAndAdjustDisableDateTime = () => {
  if (!props.disableEnabled || !props.publishEnabled) {
    return;
  }
  
  if (!props.publishDate?.date || !props.publishDate?.time || !props.disableDate?.date || !props.disableDate?.time) {
    return;
  }
  
  const publishDateStr = moment(props.publishDate.date).format('YYYY-MM-DD');
  const publishTimeStr = props.publishDate.time;
  const publishDateTime = moment.utc(`${publishDateStr} ${publishTimeStr}`, 'YYYY-MM-DD h:mm A');
  
  const disableDateStr = moment(props.disableDate.date).format('YYYY-MM-DD');
  const disableTimeStr = props.disableDate.time;
  const disableDateTime = moment.utc(`${disableDateStr} ${disableTimeStr}`, 'YYYY-MM-DD h:mm A');
  
  // If disable date/time is less than or equal to publish date/time, adjust it
  if (disableDateTime.isSameOrBefore(publishDateTime)) {
    const newDisableDateTime = publishDateTime.clone().add(1, 'hour');
    
    // Check if we need to update the date as well (in case adding 1 hour crosses midnight)
    const needDateUpdate = !moment(props.disableDate.date).isSame(newDisableDateTime, 'day');
    
    if (needDateUpdate) {
      emit('update:disableDate', {
        key: 'disable',
        index: null,
        val: newDisableDateTime.format('MM/DD/YYYY')
      });
    }
    
    emit('update:disableTime', {
      key: 'disable',
      val: newDisableDateTime.format('h:mm A')
    });
  }
};

const handlePublishToggle = () => {
  if (props.showPublishSwitch) {
    emit('toggle:publish');
    
    // Validate disable date/time after publish toggle changes
    setTimeout(() => validateAndAdjustDisableDateTime(), 0);
  }
};

const handleDisableToggle = () => {
  if (props.showDisableSwitch) {
    emit('toggle:disable');
    
    // Validate disable date/time after disable toggle changes
    setTimeout(() => validateAndAdjustDisableDateTime(), 0);
  }
};

const handlePublishDateUpdate = (date) => {
  emit('update:publishDate', date);
  
  // Auto-set time if not already set
  if (!props.publishDate?.time || !props.publishDate?.val) {
    const selectedDate = moment(date.val, 'MM/DD/YYYY');
    const currentUtc = moment.utc();
    
    // Check if selected date is today
    const isToday = selectedDate.isSame(currentUtc, 'day');
    
    let defaultTime;
    if (isToday) {
      // If today, set to next available hour (current hour + 1)
      const nextHour = currentUtc.hours() + 1;
      defaultTime = moment().hours(nextHour).minutes(0).format('h:mm A');
    } else {
      // If future date, default to 12:00 PM
      defaultTime = '12:00 PM';
    }
    
    // Emit time update
    emit('update:publishTime', {
      key: 'publish',
      val: defaultTime
    });
  }
  
  // Validate disable date/time after publish date changes
  setTimeout(() => validateAndAdjustDisableDateTime(), 0);
};

const handlePublishTimeUpdate = (time) => {
  emit('update:publishTime', time);
  
  // Validate disable date/time after publish time changes
  setTimeout(() => validateAndAdjustDisableDateTime(), 0);
};

const handleDisableDateUpdate = (date) => {
  emit('update:disableDate', date);
  
  // Auto-set time if not already set
  if (!props.disableDate?.time || !props.disableDate?.val) {
    const selectedDate = moment.utc(date.val, 'MM/DD/YYYY');
    const currentUtc = moment.utc();
    
    let defaultTime;
    
    // If publish date exists, set to at least one hour after publish time
    if (props.publishDate?.date && props.publishDate?.time) {
      const publishDateStr = moment(props.publishDate.date).format('YYYY-MM-DD');
      const publishTimeStr = props.publishDate.time;
      const publishDateTime = moment.utc(`${publishDateStr} ${publishTimeStr}`, 'YYYY-MM-DD h:mm A');
      const minDisableDateTime = publishDateTime.clone().add(1, 'hour');
      
      // Check if selected disable date is the same as publish date
      const isSameAsPublish = selectedDate.isSame(publishDateTime, 'day');
      
      if (isSameAsPublish) {
        // Use publish time + 1 hour
        defaultTime = minDisableDateTime.format('h:mm A');
      } else {
        // Future date, default to 12:00 PM
        defaultTime = '12:00 PM';
      }
    } else {
      // No publish date, follow same logic as publish date
      const isToday = selectedDate.isSame(currentUtc, 'day');
      
      if (isToday) {
        // If today, set to next available hour (current hour + 1)
        const nextHour = currentUtc.hours() + 1;
        defaultTime = moment.utc().hours(nextHour).minutes(0).format('h:mm A');
      } else {
        // If future date, default to 12:00 PM
        defaultTime = '12:00 PM';
      }
    }
    
    // Emit time update
    emit('update:disableTime', {
      key: 'disable',
      val: defaultTime
    });
  }
  
  // Validate disable date/time after disable date changes
  setTimeout(() => validateAndAdjustDisableDateTime(), 0);
};

const handleDisableTimeUpdate = (time) => {
  emit('update:disableTime', time);
  
  // Validate disable date/time after disable time changes
  setTimeout(() => validateAndAdjustDisableDateTime(), 0);
};
</script>

<style lang="scss">
.ui-schedule {
  .schedule-row {
    display: flex;
    gap: 16px;
    color: var(--Text-primary);
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }

    .schedule-left {
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;
      cursor: pointer;
      max-height: 34px;
    }

    .schedule-right {
      display: flex;
      align-items: center;
      min-height: 34px;
      width: 320px;
      flex: 1;
      position: relative;

      &.has-datepicker {
        padding-bottom: 22px;
      }

      .schedule-status {
        color: var(--Text-primary);
        font: var(--medium-14);
        display: block;
        text-align: right;
        flex: 1;
      }

      .schedule-utc-text {
        color: var(--Text-default);
        font-size: 12px;
        text-align: right;
        position: absolute;
        right: 0px;
        bottom: 4px;
        white-space: nowrap;
      }

      p {
        color: var(--Text-secondary);
        font: var(--regular-14);
        margin: 0;
      }
    }
  }

  .mx-icon-clear {
    display: none !important;

    & + i {
      display: block !important;
    }
  }
}
</style>
